import type { CanvasStoreContext } from '../types';
import type { CanvasCommand, ExecutedCommand } from './types';
import { getUndoPolicy } from './types';
import { computeDelta, applyDelta, mergeTransactionDeltas } from './computeDelta';
import { executeCommand } from './registry';
import type { CommandHistory } from './commandHistory';
import { createCommandLog, type CommandLog } from './commandLog';

// ============================================
// Deferred Transactions
// ============================================
//
// A "deferred" transaction collects commands without applying them to the store.
// Instead, each command's reducer runs against a local shadow copy of the context.
// On commit(), a single `state.restore` is sent to the store, which triggers
// exactly ONE React render — regardless of how many commands were dispatched.
//
// This is critical for the drag-end path where batchMove + batchMoveCollapsed +
// N × group.setMembers would otherwise cause N+2 synchronous React reconciliations.
//
// Code that needs to read the current state during a deferred transaction must use
// txManager.getContext() instead of store.getSnapshot().context. The drag handler
// and dropNodesIntoGroup follow this pattern.

export interface TransactionManager {
  /** Dispatch a typed command. Auto-wraps in transaction if none active. */
  dispatch(command: CanvasCommand): void;

  /**
   * Begin an explicit transaction. All dispatches until commit() form one undo step.
   *
   * @param label - Human-readable label for the undo stack entry.
   * @param deferred - When true, commands are executed against a local shadow context
   *   and only flushed to the store on commit(). This avoids multiple React renders.
   */
  begin(label?: string, deferred?: boolean): void;
  commit(): void;
  rollback(): void;
  isInTransaction(): boolean;

  /**
   * Returns the current effective context.
   * - During a deferred transaction: the shadow context (includes uncommitted changes).
   * - Otherwise: the store's actual context.
   *
   * Use this instead of store.getSnapshot().context when you need to read state
   * that may have been modified by earlier commands in the same deferred transaction.
   */
  getContext(): CanvasStoreContext;

  undo(): void;
  redo(): void;

  readonly history: CommandHistory;
  readonly commandLog: CommandLog;
}

interface StoreInterface {
  send: (event: any) => void;
  getSnapshot: () => { context: CanvasStoreContext };
}

export function createTransactionManager(
  store: StoreInterface,
  originalSend: (event: any) => void,
  history: CommandHistory,
): TransactionManager {
  const commandLog = createCommandLog();

  interface ActiveTransaction {
    id: string;
    label: string;
    commands: ExecutedCommand[];
    deferred: boolean;
    /** In deferred mode, holds the accumulated state. null in normal mode. */
    shadowCtx: CanvasStoreContext | null;
  }

  let activeTx: ActiveTransaction | null = null;
  let txCounter = 0;

  // ── getContext ──────────────────────────────────────────────────────

  function getContext(): CanvasStoreContext {
    if (activeTx?.deferred && activeTx.shadowCtx) {
      return activeTx.shadowCtx;
    }
    return store.getSnapshot().context;
  }

  // ── dispatch ───────────────────────────────────────────────────────

  function dispatch(command: CanvasCommand) {
    commandLog.push({
      command,
      timestamp: Date.now(),
      actor: { type: 'user' },
      transactionId: activeTx?.id,
      transactionLabel: activeTx?.label,
    });

    const policy = getUndoPolicy(command.type);

    // ── Deferred mode: run reducer locally, don't touch the store ──
    if (activeTx?.deferred) {
      const before = activeTx.shadowCtx!;
      const after = executeCommand(before, command);
      activeTx.shadowCtx = after;

      if (policy !== 'non-undoable') {
        const delta = computeDelta(before, after);
        if (delta) {
          activeTx.commands.push({ command, delta });
        }
      }
      return;
    }

    // ── Non-undoable: fire-and-forget ──
    if (policy === 'non-undoable') {
      originalSend(command);
      return;
    }

    // ── Normal mode: execute via store ──
    const before = store.getSnapshot().context;
    originalSend(command);
    const after = store.getSnapshot().context;
    const delta = computeDelta(before, after);

    if (delta) {
      const executed: ExecutedCommand = { command, delta };
      if (activeTx) {
        activeTx.commands.push(executed);
      } else {
        history.pushTransaction({
          label: command.type,
          commands: [executed],
          timestamp: Date.now(),
        });
      }
    }
  }

  // ── begin / commit / rollback ──────────────────────────────────────

  function begin(label?: string, deferred?: boolean) {
    if (activeTx) return; // nested begin: keep existing transaction
    activeTx = {
      id: `tx-${++txCounter}`,
      label: label ?? 'transaction',
      commands: [],
      deferred: deferred ?? false,
      shadowCtx: deferred ? store.getSnapshot().context : null,
    };
  }

  function commit() {
    if (!activeTx) return;
    const tx = activeTx;
    activeTx = null;

    if (tx.commands.length === 0) return;

    if (tx.deferred) {
      // Apply all accumulated changes as a single store update → 1 React render
      originalSend({ type: 'state.restore', context: tx.shadowCtx });
    }

    history.pushTransaction({
      label: tx.label,
      commands: tx.commands,
      timestamp: Date.now(),
    });
  }

  function rollback() {
    if (!activeTx) return;
    const tx = activeTx;
    activeTx = null;

    if (tx.deferred) {
      // Shadow context was never applied to the store — nothing to revert
      return;
    }

    if (tx.commands.length > 0) {
      const merged = mergeTransactionDeltas(tx.commands);
      const current = store.getSnapshot().context;
      const restored = applyDelta(current, merged, 'reverse');
      originalSend({ type: 'state.restore', context: restored });
    }
  }

  // ── undo / redo ────────────────────────────────────────────────────

  function undo() {
    const tx = history.undo();
    if (!tx) return;
    const merged = mergeTransactionDeltas(tx.commands);
    const current = store.getSnapshot().context;
    const restored = applyDelta(current, merged, 'reverse');
    originalSend({ type: 'state.restore', context: restored });
  }

  function redo() {
    const tx = history.redo();
    if (!tx) return;
    const merged = mergeTransactionDeltas(tx.commands);
    const current = store.getSnapshot().context;
    const restored = applyDelta(current, merged, 'forward');
    originalSend({ type: 'state.restore', context: restored });
  }

  // ── Public API ─────────────────────────────────────────────────────

  return {
    dispatch,
    begin,
    commit,
    rollback,
    isInTransaction: () => activeTx !== null,
    getContext,
    undo,
    redo,
    history,
    commandLog,
  };
}
